/* AiGenerateButton.jsx — Wave CB · "Generate with AI" button shown on
   every deliverable in every stage panel.

   Architecture (per user request):
     - User clicks "Generate with AI" on ONE deliverable
     - Backend kicks off Claude Opus 4.6 in a BackgroundTask, returns
       immediately with `status: "running"`
     - Frontend polls `/ai-status` every 5 s (max 4 min)
     - On `done`, the deliverable content_md is replaced with the
       AI-enriched narrative; the calling page can re-fetch
       deliverables to render the new content.
*/
import React, { useEffect, useState, useRef } from "react";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import api from "../lib/api";

export default function AiGenerateButton({
  projectId,
  deliverableCode,
  stageCode,
  initialAiEnriched,
  initialAiAt,
  btnStyle,
}) {
  const [state, setState] = useState({
    running: false,
    enriched: !!initialAiEnriched,
    at: initialAiAt || null,
    error: null,
    elapsed: 0,
  });
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const stopTick = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const startTick = (startTs) => {
    stopTick();
    tickRef.current = setInterval(() => {
      setState((s) => ({ ...s, elapsed: Math.round((Date.now() - startTs) / 1000) }));
    }, 1000);
  };

  const poll = async () => {
    try {
      const r = await api.get(
        `/projects/${projectId}/deliverables/${deliverableCode}/ai-status`
      );
      const data = r.data;
      if (data.job_status === "done") {
        setState({
          running: false,
          enriched: true,
          at: data.ai_generated_at,
          error: null,
          elapsed: 0,
        });
        stopTick();
        if (pollRef.current) clearInterval(pollRef.current);
        // Reload page deliverables so the new content appears
        window.dispatchEvent(new CustomEvent("deliverable-ai-done", {
          detail: { code: deliverableCode, stageCode }
        }));
      } else if (data.job_status === "error") {
        setState((s) => ({
          ...s,
          running: false,
          error: data.job_error || "AI generation failed",
        }));
        stopTick();
        if (pollRef.current) clearInterval(pollRef.current);
      }
    } catch (e) {
      // Ignore transient errors, keep polling
    }
  };

  const start = async () => {
    setState((s) => ({ ...s, running: true, error: null, elapsed: 0 }));
    try {
      const r = await api.post(
        `/projects/${projectId}/deliverables/${deliverableCode}/generate-ai`,
        {}
      );
      if (r.data?.status === "running" || r.data?.status === "done") {
        const startTs = Date.now();
        startTick(startTs);
        // Poll every 5 s
        pollRef.current = setInterval(poll, 5000);
        // First poll after 8 s
        setTimeout(poll, 8000);
      }
    } catch (e) {
      setState((s) => ({
        ...s,
        running: false,
        error: e?.response?.data?.detail || e?.message || "AI generation failed",
      }));
    }
  };

  useEffect(() => {
    return () => {
      stopTick();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Style variations
  const enrichedStyle = {
    ...btnStyle,
    background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
    color: "#fff",
    border: "1px solid #7c3aed",
  };
  const runningStyle = {
    ...btnStyle,
    background: "var(--pf-spark)",
    color: "#fff",
    border: "1px solid var(--pf-spark)",
    cursor: "wait",
  };
  const errStyle = {
    ...btnStyle,
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #ef4444",
  };

  if (state.running) {
    return (
      <button type="button" disabled style={runningStyle}
              data-testid={`deliv-ai-running-${deliverableCode}`}
              title="Claude Opus 4.6 is generating — typically 30-90 seconds">
        <Loader2 size={14} className="animate-spin" />
        Generating… {state.elapsed}s
      </button>
    );
  }
  if (state.error) {
    return (
      <button type="button" onClick={start} style={errStyle}
              data-testid={`deliv-ai-retry-${deliverableCode}`}
              title={state.error}>
        <AlertTriangle size={14} /> Retry AI
      </button>
    );
  }
  if (state.enriched) {
    return (
      <button type="button" onClick={start} style={enrichedStyle}
              data-testid={`deliv-ai-regen-${deliverableCode}`}
              title={`AI-enriched ${state.at ? `at ${new Date(state.at).toLocaleString()}` : ""} · click to re-generate`}>
        <CheckCircle2 size={14} /> AI ✓ · Regen
      </button>
    );
  }
  return (
    <button type="button" onClick={start} style={{
      ...btnStyle,
      background: "linear-gradient(135deg, #f97316 0%, #fbbf24 100%)",
      color: "#1e293b",
      border: "1px solid #f97316",
      fontWeight: 600,
    }}
            data-testid={`deliv-ai-gen-${deliverableCode}`}
            title="Generate detailed engineering narrative with Claude Opus 4.6 — uses the deterministic engineering numbers as authoritative inputs">
      <Sparkles size={14} /> Generate with AI
    </button>
  );
}
